import type { StoreActionApi } from 'react-sweet-state';

import {
	type ActiveOverride,
	PageModalType,
	type RotationBase,
	type ShiftContext,
} from '../../../common/types';
import { type ScheduleEntity } from '../../../services/endpoints/list/types';
import { initialState } from '../state';
import {
	CreateVariant,
	type DiscardVariant,
	OverrideVariant,
	type PageStore,
} from '../state/types';
import { type ContainerProps } from '../types';

type Source = {
	/** @description id of the source (triggering HTML element) */
	id?: string | string[] | null | undefined;
	/** @description To replace instead of add */
	replace?: boolean;
};

const combine = (current: PageStore['sources'], { id, replace }: Source) =>
	replace ? current.slice(0, -1).concat([id]) : current.concat([id]);

const openCreateSchedule =
	({ id, replace }: Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			isCreateScheduleOpen: true,
			createVariant: CreateVariant.Create,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openCloneSchedule =
	({ schedule, id, replace }: { schedule: ScheduleEntity } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			isCreateScheduleOpen: true,
			createVariant: CreateVariant.Clone,
			activeSchedule: schedule,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openEditMode =
	({ schedule, id, replace }: { schedule: ScheduleEntity } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			activeSchedule: schedule,
			isEditModeOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openTakeOnCall =
	({ schedule, id, replace }: { schedule: ScheduleEntity } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			activeSchedule: schedule,
			isTakeOnCallOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openAddOverride =
	({ schedule, id, replace }: { schedule: ScheduleEntity } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			activeSchedule: schedule,
			overrideVariant: OverrideVariant.Add,
			isAddOverrideOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openEditOverride =
	({
		override,
		context,
		id,
		replace,
	}: {
		override: ActiveOverride<true, true>;
		context: ShiftContext;
	} & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>, { schedules }: ContainerProps) => {
		// TODO: add assertion
		const schedule = schedules.find(({ id }) => context.scheduleId === id);

		setState({
			activeSchedule: schedule,
			activeOverride: override,
			overrideVariant: OverrideVariant.Edit,
			isAddOverrideOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openRevertOverride =
	({
		override,
		context,
		id,
		replace,
	}: {
		override: ActiveOverride<true, true>;
		context: ShiftContext;
	} & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>, { schedules }: ContainerProps) => {
		// TODO: add assertion
		const schedule = schedules.find(({ id }) => context.scheduleId === id);

		setState({
			activeSchedule: schedule,
			activeOverride: override,
			isRevertOverrideOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openCustomizeOverride =
	({
		override,
		context,
		id,
		replace,
	}: {
		override: ActiveOverride<false, false>;
		context: ShiftContext;
	} & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>, { schedules }: ContainerProps) => {
		// TODO: add assertion
		const schedule = schedules.find(({ id }) => context.scheduleId === id);

		setState({
			activeSchedule: schedule,
			activeOverride: override,
			overrideVariant: OverrideVariant.Customize,
			isAddOverrideOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openDeleteSchedule =
	({ schedule, id, replace }: { schedule?: ScheduleEntity } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		if (schedule) {
			setState({
				activeSchedule: schedule,
				isDeleteScheduleOpen: true,
				sources: combine(getState().sources, { id, replace }),
			});
		} else {
			setState({
				isDeleteScheduleOpen: true,
				sources: combine(getState().sources, { id, replace }),
			});
		}
	};

const openDeleteRotation =
	({ rotation, id, replace }: { rotation: RotationBase } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			activeRotation: rotation,
			isDeleteRotationOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const openDiscardChanges =
	({ variant, id, replace }: { variant: DiscardVariant } & Source) =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		setState({
			discardVariant: variant,
			isDiscardChangesOpen: true,
			sources: combine(getState().sources, { id, replace }),
		});
	};

const closePageModal =
	(modal: PageModalType) =>
	({ setState }: StoreActionApi<PageStore>) => {
		switch (modal) {
			case PageModalType.CreateSchedule:
				setState({
					isCreateScheduleOpen: false,
					createVariant: undefined,
					activeSchedule: undefined,
				});
				break;
			case PageModalType.AddOverride:
				setState({
					isAddOverrideOpen: false,
					activeSchedule: undefined,
					overrideVariant: undefined,
					activeOverride: undefined,
				});
				break;
			case PageModalType.DeleteSchedule:
				setState({
					isDeleteScheduleOpen: false,
				});
				break;
			case PageModalType.DeleteRotation:
				setState({
					isDeleteRotationOpen: false,
					activeRotation: undefined,
				});
				break;
			case PageModalType.DiscardChanges:
				setState({
					isDiscardChangesOpen: false,
					discardVariant: undefined,
				});
				break;
			case PageModalType.EditMode:
				setState({
					isEditModeOpen: false,
					activeSchedule: undefined,
				});
				break;
			case PageModalType.RevertOverride:
				setState({
					isRevertOverrideOpen: false,
					activeSchedule: undefined,
					activeOverride: undefined,
				});
				break;
			case PageModalType.TakeOnCall:
				setState({
					isTakeOnCallOpen: false,
					activeSchedule: undefined,
				});
				break;
			default:
				setState(initialState);
		}
	};

const focusSource =
	() =>
	({ setState, getState }: StoreActionApi<PageStore>) => {
		const stack = [...getState().sources];
		const queue = stack.pop();

		if (queue) {
			if (Array.isArray(queue)) {
				window.setTimeout(
					(refs: string[]) => {
						while (Array.isArray(refs) && refs.length > 0) {
							const id = refs.shift();

							if (!id) {
								continue;
							}

							const element = document.getElementById(id);

							if (element && document.body.contains(element)) {
								element.focus();
								break;
							}
						}
					},
					0,
					queue,
				);
			} else {
				if (queue) {
					window.setTimeout(
						(id: string) => {
							if (!id) {
								return;
							}

							const element = document.getElementById(id);

							if (element && document.body.contains(element)) {
								element.focus();
							}
						},
						0,
						queue,
					);
				}
			}
		}

		setState({ sources: stack });
	};

export const actions = {
	closePageModal,
	focusSource,
	openAddOverride,
	openCreateSchedule,
	openCloneSchedule,
	openCustomizeOverride,
	openDeleteSchedule,
	openEditMode,
	openEditOverride,
	openRevertOverride,
	openTakeOnCall,
	openDeleteRotation,
	openDiscardChanges,
};
